import { saveBase64 } from '../../platform/storage';
import type { FileSubject } from '../../platform/storage';
import { ziehAbzug } from '../../platform/screenshot';
import { planTiles } from './tiles';

/**
 * Abzug des Bogens beim Absenden.
 *
 * Der Bogen ist meist länger als der Bildschirm. Statt eines Bildes, das nur den oberen Teil
 * zeigt, entstehen so viele Aufnahmen, wie nötig sind, um alles einmal zu zeigen — und keine
 * mehr (`tiles.ts` rechnet aus, wohin gescrollt wird).
 *
 * ⚠️ **Kopf- und Fusszeile stehen auf jeder Aufnahme.** Sie wegzublenden gäbe ruhigere
 * Bilder; so aber trägt jede einzelne Aufnahme Prüfling, Station und Gesamtpunktzahl und ist
 * für sich zuzuordnen. Bei einem Beweisdokument wiegt das schwerer als Wiederholung.
 *
 * ⚠️ **Es wird abgezogen, was auf dem Schirm steht** — nicht ein neu gezeichnetes Abbild.
 * Genau das macht es zum Beleg dessen, was der Prüfer gesehen hat.
 */

/** Das scrollende Element des Bogens. `Bogen.tsx` setzt die Marke. */
export const BOGEN_SCROLL_MARKE = 'data-bogen-scroll';

/** Der Anlass, aus dem ein Abzug entsteht — er steht mit auf jeder Kachel. */
export type Abzugsgrund =
  | 'Abgabe'
  | 'Prüflingswechsel'
  | 'Stationswechsel'
  | 'Prüfungswechsel';

export interface Abzugsangaben {
  /** Der in den Einstellungen vergebene iPad-Name. */
  geraet: string;
  /** Zeitpunkt des Abzugs — nicht der des Schreibens; die können sich um Sekunden trennen. */
  zeitpunkt: Date;
  station: string;
  pruefer: string;
  pruefling: string;
  grund: Abzugsgrund | string;
}

/**
 * Die Zeile, die auf **jeder** Kachel steht.
 *
 * ⚠️ **Der Anlass gehört hinein, weil ihn sonst nichts trägt.** Dateiname und Manifest kennen
 * nur die Art „screenshot"; ob abgegeben oder gewechselt wurde, stünde nirgends. Ein
 * Wechsel-Abzug sähe später aus wie eine Abgabe — und damit wie eine Bewertung, die der
 * Prüfling nie bekommen hat.
 *
 * ⚠️ **Fehlende Angaben werden zu „—", nicht weggelassen.** Anders als bei der Beschriftung
 * der Fotos-Mediathek (`platform/fotos.ts`, wo Zeilen entfallen dürfen) ist dies **eine**
 * Zeile fester Ordnung: liesse man ein Feld weg, rückte der Prüfling an die Stelle des
 * Prüfers, und wer die Zeile später liest, verwechselte beide. Nur ein fehlender **Gerätename**
 * entfällt (2026-10-02): er steht vorn, das Datum danach ist unverwechselbar.
 *
 * ⚠️ **Keine Ortsschreibweise.** `toLocaleDateString()` gäbe je nach Gerätesprache ein
 * anderes Datum — auf einem Beleg soll dieselbe Zeile überall gleich aussehen. Die Sekunden
 * bleiben drin: sie ordnen den Abzug seiner Datei zu.
 */
export function baueAbzugBeschriftung(a: Abzugsangaben): string {
  const p = (v: number) => String(v).padStart(2, '0');
  const d = a.zeitpunkt;
  const zeit =
    p(d.getDate()) + '.' + p(d.getMonth() + 1) + '.' + d.getFullYear() +
    ' ' + p(d.getHours()) + ':' + p(d.getMinutes()) + ':' + p(d.getSeconds());
  const oder = (wert: string) => (wert.trim() === '' ? '—' : wert);
  // Ausnahme Gerät: es steht vorn, und dahinter folgt immer das Datum — da rückt nichts an eine
  // falsche Stelle. „—  ·" am Zeilenanfang sagte nur, dass kein `device_name` gesetzt ist.
  const geraet = a.geraet.trim() === '' ? [] : [a.geraet];
  return [...geraet, zeit, oder(a.station), oder(a.pruefer), oder(a.pruefling), a.grund]
    .join('  ·  ');
}

export interface ShotDeps {
  /** Wo gescrollt wird. Fehlt es, gibt es nichts abzuziehen. */
  element: () => HTMLElement | null;
  capture: () => Promise<{ base64: string; format: 'jpg' | 'png' }>;
  /**
   * ⚠️ **Die Endung kommt von der Aufnahme, nicht von hier.** Schlägt das JPEG-Kodieren fehl,
   * liefert der Erzeuger PNG — und eine `.jpg`-Datei mit PNG-Inhalt trüge beim Hochladen den
   * falschen Medientyp.
   */
  save: (
    subject: FileSubject,
    part: number | undefined,
    base64: string,
    endung: string,
  ) => Promise<string>;
  /** Ein Bild pro Zeichenlauf: ohne Warten zeigt der Abzug noch die alte Scrollstellung. */
  warteAufBild: () => Promise<void>;
  /**
   * Legt die Sperrscheibe samt Beschriftung über den Bogen und wartet, bis sie steht.
   *
   * Sie tut zweierlei: sie fängt jede Berührung ab, solange der Bogen unter der Hand
   * wegscrollt — ein Häkchen, das währenddessen gesetzt wird, landete sonst auf einer
   * Aufgabe, die der Prüfer gar nicht sieht — und sie trägt die Beschriftung, die auf
   * **jeder** Kachel stehen soll.
   */
  beginn: (beschriftung: string) => Promise<void>;
  /** Nimmt sie wieder weg. Läuft auch, wenn die Aufnahme scheitert. */
  ende: () => void;
  log: (text: string) => Promise<void>;
}

/**
 * Nimmt den Bogen auf und gibt die Pfade der geschriebenen Dateien zurück.
 *
 * ⚠️ **Zwei Abschnitte, und das ist der Punkt.** Zuerst wird nur aufgenommen: scrollen,
 * abziehen, im Speicher behalten. Erst danach — Sperrscheibe schon weg — wird geschrieben
 * und beglaubigt. Der Prüfer wartet dadurch nur auf den kurzen Teil.
 *
 * Ein echter Nebenläufer (Web Worker) hilft hier **nicht**: er kommt weder an das DOM noch
 * an die native Brücke, und beides ist genau das, was der Abzug braucht. Was geht, ist das
 * Verschieben in die Zeit nach der Sperre — und das bringt dasselbe, worum es geht.
 *
 * Wirft nicht: ein fehlgeschlagener Abzug darf das Absenden nicht aufhalten — die Bewertung
 * ist zu diesem Zeitpunkt bereits geschrieben, und ein Prüfling wartet auf den nächsten
 * Schritt. Der Fehlschlag geht ins Dateilog.
 */
export async function captureBogen(
  deps: ShotDeps,
  subject: FileSubject,
  beschriftung: string,
): Promise<string[]> {
  const element = deps.element();
  if (!element) {
    await deps.log('Bogenabzug: kein Bogen auf dem Schirm, nichts aufgenommen.');
    return [];
  }

  const vorher = element.scrollTop;
  // Format je Kachel, nicht je Abzug: der Rückfall auf PNG entscheidet sich bei jeder
  // einzelnen Aufnahme.
  const bilder: { base64: string; format: 'jpg' | 'png' }[] = [];
  let stellen: number[] = [];

  // — Abschnitt 1: nur aufnehmen, so kurz wie möglich —
  try {
    await deps.beginn(beschriftung);
    // ⚠️ Erst **nach** `beginn` messen: im Abzug stehen die Kommentartexte offen
    // (`TaskCard.tsx::AbzugKommentar`), der Bogen ist dann länger als davor.
    stellen = planTiles(element.scrollHeight, element.clientHeight);
    for (const stelle of stellen) {
      element.scrollTop = stelle;
      await deps.warteAufBild();
      bilder.push(await deps.capture());
    }
  } catch (fehler) {
    await deps.log(
      'Bogenabzug nach ' + bilder.length + ' von ' + stellen.length +
        ' Aufnahmen abgebrochen: ' + String(fehler),
    );
  } finally {
    element.scrollTop = vorher;
    deps.ende();
  }

  // — Abschnitt 2: schreiben und beglaubigen, ohne dass jemand darauf wartet —
  const pfade: string[] = [];
  for (const [i, bild] of bilder.entries()) {
    // Nur bei mehreren Teilen eine Nummer: eine einzelne Aufnahme soll nicht aussehen,
    // als fehlte der Rest.
    const teil = bilder.length > 1 ? i + 1 : undefined;
    try {
      pfade.push(await deps.save(subject, teil, bild.base64, bild.format));
    } catch (fehler) {
      // Was schon geschrieben ist, bleibt liegen: ein halber Abzug ist mehr wert als keiner,
      // und die Teilnummern im Dateinamen zeigen, dass etwas fehlt.
      await deps.log('Bogenabzug: Teil ' + (i + 1) + ' nicht geschrieben: ' + String(fehler));
    }
  }

  if (pfade.length > 0) {
    await deps.log(
      'Bogenabzug: ' + pfade.length + ' Aufnahme' + (pfade.length === 1 ? '' : 'n') + ' geschrieben.',
    );
  }
  return pfade;
}

/** Die Verdrahtung auf dem Gerät. */
export function bogenShotDeps(
  log: (text: string) => Promise<void>,
  scheibe: { zeigen: (beschriftung: string) => void; verbergen: () => void },
): ShotDeps {
  const warteAufBild = () =>
    new Promise<void>((fertig) => {
      requestAnimationFrame(() => requestAnimationFrame(() => fertig()));
    });

  return {
    // Ohne DOM gibt es keinen Bogen: im Testlauf (`environment: 'node'`) läuft
    // `submitAssessment` mit, und ein Zugriff auf `document` risse es mit einem
    // ReferenceError ab. `captureBogen` schreibt dann eine Zeile ins Dateilog und ist fertig
    // — geprüft wird der Abzug selbst in `bogenShot.test.ts` gegen Attrappen, nicht hier.
    element: () =>
      typeof document === 'undefined'
        ? null
        : document.querySelector<HTMLElement>('[' + BOGEN_SCROLL_MARKE + ']'),
    capture: () => ziehAbzug(),
    save: (subject, part, base64, endung) =>
      saveBase64('station', 'screenshot', { ...subject, part }, endung, base64),
    // Zwei Bilder abwarten: das erste verarbeitet die Scroll-Änderung, im zweiten steht sie
    // wirklich auf dem Schirm. Mit nur einem zeigte der Abzug gelegentlich die alte Stelle.
    warteAufBild,
    beginn: async (beschriftung) => {
      scheibe.zeigen(beschriftung);
      // Erst wenn die Scheibe wirklich steht, darf gescrollt werden — sonst zeigte die
      // erste Kachel sie noch nicht und trüge keine Beschriftung.
      await warteAufBild();
    },
    ende: () => scheibe.verbergen(),
    log,
  };
}
